import React, { useEffect, useRef, useState } from "react";

import "leaflet/dist/leaflet.css";
import "./MapaRotaEstimada.css";

// Mapa da rota ESTIMADA entre origem e destino (PO1-2818, épico PO1-2817).
//
// Não é rastreamento em tempo real e nunca deve ser apresentado como tal: não
// existe GPS/telemetria do veículo em lugar nenhum do Hub hoje. O que temos é o
// município de origem (endereço do embarcador remetente) e o de destino
// (endereço de entrega da NF), então o traçado é uma linha reta entre os
// centróides dos dois municípios — ilustrativo, não o caminho percorrido. Por
// isso a linha é tracejada e a legenda diz "rota estimada".
//
// As coordenadas vêm de um dataset ESTÁTICO de centróide por município IBGE
// (`centroidesIbge.json`), cruzado pelo `Cod_IBGE` que a API já devolve. Não há
// geocoding em runtime: seria uma dependência externa nova numa página pública,
// com custo e latência por acesso.
//
// O dataset tem ~160 KB, então é carregado sob demanda (`import()` dinâmico, que
// o webpack separa em chunk próprio) — só pesa para quem realmente abre um
// rastreio com origem e destino conhecidos.

const ZOOM_MAXIMO = 11;

// Fundo do mapa: sempre o OSM padrão. No tema escuro do Hub o OSM vira um
// bloco claro no meio da tela, então a tela interna pede `tema="escuro"` e o
// CSS escurece só a camada de tiles (filtro), sem trocar de provedor: o basemap
// escuro da CARTO passou a exigir chave de API (tiles com marca d'água "API KEY
// REQUIRED", visto no QA de 29/09/2026).
const CAMADA_OSM = {
  url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
  attribution: "&copy; OpenStreetMap",
};

// O Leaflet desenha marcador e linha via JS e só aceita cor como string — não
// dá para passar `var(--token)` direto. Lemos o token computado do tema para o
// mapa acompanhar claro/escuro como o resto da página. O fallback só entra se a
// variável não existir (ex.: teste sem CSS carregado).
function corDoTema(token, fallback) {
  if (typeof window === "undefined" || !window.getComputedStyle) return fallback;
  const valor = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(token);

  return (valor && valor.trim()) || fallback;
}

// Fallback de última instância do Leaflet, usado só se o token do tema não
// resolver; a cor real vem de corDoTema() acima.
const COR_ORIGEM_FALLBACK = "#8f9bba"; // theme-ok: fallback do Leaflet, cor real vem do token
const COR_DESTINO_FALLBACK = "#1d8cf8"; // theme-ok: fallback do Leaflet, cor real vem do token

/**
 * Props:
 * - origem, destino: `{ ibge, cidade, uf }`.
 * - tema: "escuro" escurece os tiles por CSS (tela interna no tema escuro).
 *   Sem a prop, OSM padrão.
 * - compacto: sem cabeçalho nem margem, com legenda sobre o mapa; para quando o
 *   card que o contém já mostra a rota (Rastreamento interno).
 */
const MapaRotaEstimada = ({ origem, destino, tema, compacto = false }) => {
  const containerRef = useRef(null);
  const mapaRef = useRef(null);
  const observadorRef = useRef(null);
  const [coordenadas, setCoordenadas] = useState(null);
  const [indisponivel, setIndisponivel] = useState(false);

  const ibgeOrigem = origem && origem.ibge ? String(origem.ibge) : null;
  const ibgeDestino = destino && destino.ibge ? String(destino.ibge) : null;

  useEffect(() => {
    let cancelado = false;

    if (!ibgeOrigem || !ibgeDestino) {
      setIndisponivel(true);
      return undefined;
    }

    async function carregarCentroides() {
      try {
        const modulo = await import("./centroidesIbge.json");
        if (cancelado) return;

        const base = modulo.default || modulo;
        const pontoOrigem = base[ibgeOrigem];
        const pontoDestino = base[ibgeDestino];

        if (!pontoOrigem || !pontoDestino) {
          setIndisponivel(true);
          return;
        }

        setCoordenadas({ origem: pontoOrigem, destino: pontoDestino });
      } catch (_) {
        if (!cancelado) setIndisponivel(true);
      }
    }

    carregarCentroides();

    return () => {
      cancelado = true;
    };
  }, [ibgeOrigem, ibgeDestino]);

  useEffect(() => {
    if (!coordenadas || !containerRef.current || mapaRef.current) return undefined;

    let cancelado = false;

    async function montarMapa() {
      // Leaflet só no cliente e só quando há o que desenhar — evita puxar a
      // biblioteca para quem abre um rastreio sem município resolvido.
      const modulo = await import("leaflet");
      if (cancelado || !containerRef.current) return;

      const L = modulo.default || modulo;

      const mapa = L.map(containerRef.current, {
        scrollWheelZoom: false,
        zoomControl: true,
        attributionControl: true,
      });
      mapaRef.current = mapa;

      L.tileLayer(CAMADA_OSM.url, {
        maxZoom: 18,
        attribution: CAMADA_OSM.attribution,
      }).addTo(mapa);

      // circleMarker em vez do marker padrão de propósito: o ícone default do
      // Leaflet resolve o caminho da imagem em runtime e quebra com o hash de
      // build do CRA. Círculo desenhado não tem esse problema e não precisa de
      // asset nenhum.
      const marcador = (ponto, cor, rotulo) =>
        L.circleMarker(ponto, {
          radius: 8,
          color: cor,
          fillColor: cor,
          fillOpacity: 0.9,
          weight: 2,
        })
          .addTo(mapa)
          .bindTooltip(rotulo, { direction: "top", offset: [0, -8] });

      const corOrigem = corDoTema("--everlog-card-muted", COR_ORIGEM_FALLBACK);
      const corDestino = corDoTema("--theme-accent", COR_DESTINO_FALLBACK);

      marcador(coordenadas.origem, corOrigem, rotuloDe(origem, "Origem"));
      marcador(coordenadas.destino, corDestino, rotuloDe(destino, "Destino"));

      L.polyline([coordenadas.origem, coordenadas.destino], {
        color: corDestino,
        weight: 3,
        opacity: 0.65,
        dashArray: "8 8",
      }).addTo(mapa);

      // `padding` em PIXELS, não `.pad()`: o `.pad(0.25)` inflava a área
      // GEOGRÁFICA em 25% antes de enquadrar, o que em rota longa forçava o
      // zoom para fora a ponto de aparecer outro continente na tela (visto no
      // QA com Joinville/SC → São Luís/MA). O padding em pixels dá a mesma
      // folga visual sem aumentar a área coberta.
      mapa.fitBounds(L.latLngBounds([coordenadas.origem, coordenadas.destino]), {
        maxZoom: ZOOM_MAXIMO,
        padding: [24, 24],
      });

      // O Leaflet mede o container UMA vez, no `L.map()`, e não reage sozinho a
      // mudança de tamanho. O card é responsivo (a altura do mapa cai de 260px
      // para 200px abaixo de 767px), então sem remedir sobra área sem tile ao
      // girar o celular ou redimensionar a janela.
      mapa.invalidateSize();

      if (typeof ResizeObserver !== "undefined") {
        observadorRef.current = new ResizeObserver(() => {
          if (mapaRef.current) mapaRef.current.invalidateSize();
        });
        observadorRef.current.observe(containerRef.current);
      }
    }

    montarMapa();

    return () => {
      cancelado = true;
      if (observadorRef.current) {
        observadorRef.current.disconnect();
        observadorRef.current = null;
      }
      if (mapaRef.current) {
        mapaRef.current.remove();
        mapaRef.current = null;
      }
    };
  }, [coordenadas, origem, destino]);

  const classe = [
    "mapa-rota-estimada",
    compacto ? "mapa-rota-estimada--compacto" : null,
    tema === "escuro" ? "mapa-rota-estimada--escuro" : null,
  ].filter(Boolean).join(" ");

  if (indisponivel) {
    return (
      <div className={`${classe} mapa-rota-estimada--empty`}>
        <i className="tim-icons icon-square-pin" aria-hidden="true" />
        <span>
          Ainda não temos a localização de origem e destino desta entrega.
        </span>
      </div>
    );
  }

  return (
    <div className={classe}>
      {compacto ? null : (
        <div className="mapa-rota-estimada__header">
          <strong>Rota prevista</strong>
          <span>
            {rotuloDe(origem, "Origem")} → {rotuloDe(destino, "Destino")}
          </span>
        </div>
      )}
      <div className="mapa-rota-estimada__stage">
        <div className="mapa-rota-estimada__canvas" ref={containerRef} />
        {compacto ? (
          <div className="mapa-rota-estimada__legend">
            <span>
              <i className="mapa-rota-estimada__dot mapa-rota-estimada__dot--origem" />
              Origem · {rotuloDe(origem, "-")}
            </span>
            <span>
              <i className="mapa-rota-estimada__dot mapa-rota-estimada__dot--destino" />
              Destino · {rotuloDe(destino, "-")}
            </span>
          </div>
        ) : null}
      </div>
      {/* Uma linha só. O texto longo anterior soava defensivo numa página de
          consumidor. O que precisa sobrar é a cláusula que evita a leitura
          errada de verdade: o ponto no mapa não é o veículo. Sem isso, dois
          marcadores e uma linha parecem rastreamento ao vivo. */}
      <p className="mapa-rota-estimada__note">
        Traçado estimado entre origem e destino. Não mostra a posição do veículo.
      </p>
    </div>
  );
};

function rotuloDe(local, padrao) {
  if (!local || !local.cidade) return padrao;
  return local.uf ? `${local.cidade}/${local.uf}` : local.cidade;
}

export default MapaRotaEstimada;
